import { z } from 'zod';

// ---------------------------------------------------------------------------
// Account plan
//
// kruxit has no payment provider yet. A plan is just a value held in Clerk
// `publicMetadata` — on the user (personal plan) and, independently, on the
// organisation (org plan). Feature gating will read it via `planFromMetadata`.
// publicMetadata is readable on the client but only writable from the backend,
// so a plan can only ever change through a backend route.
// ---------------------------------------------------------------------------

/** Plan tiers. Two for now; widen the tuple when more are introduced. */
export const Plan = ['free', 'paid'] as const;
export const PlanSchema = z.enum(Plan);
export type Plan = z.infer<typeof PlanSchema>;

/** Everyone is free unless explicitly marked otherwise. */
export const DEFAULT_PLAN: Plan = 'free';

/**
 * Read a plan from Clerk `publicMetadata` (user or organisation). When the field
 * is absent or malformed it returns `fallback` (default `free`), so an explicit
 * stored plan always wins; pass `'paid'` to give a surface its own default
 * (e.g. the maintainer's personal plan).
 */
export function planFromMetadata(
  metadata: unknown,
  fallback: Plan = DEFAULT_PLAN,
): Plan {
  const plan = (metadata as { readonly plan?: unknown } | null | undefined)
    ?.plan;
  const parsed = PlanSchema.safeParse(plan);
  return parsed.success ? parsed.data : fallback;
}

const OrgAdminIdsSchema = z.array(z.string());

/**
 * The validated list of Clerk org ids the user administers, read from
 * `publicMetadata`. Authorisation must go through this Zod-backed reader: raw
 * metadata is untyped, so a malformed value (e.g. a bare string) could otherwise
 * pass a `.includes()` check as a substring match and wrongly grant authority.
 * Absent or malformed → `[]`.
 */
export function readOrgAdminIds(metadata: unknown): readonly string[] {
  const value = (
    metadata as { readonly orgAdminIds?: unknown } | null | undefined
  )?.orgAdminIds;
  const parsed = OrgAdminIdsSchema.safeParse(value);
  return parsed.success ? parsed.data : [];
}

/**
 * Whether a surface gated behind a paid plan should unlock: true when either the
 * user's personal plan or their active organisation's plan is `paid`. Pass the
 * user's `publicMetadata` and the active org's `publicMetadata` (or `undefined`
 * when there is no active org). Pure, so it runs on the server and the client.
 */
export function hasPaidAccess(
  userMetadata: unknown,
  orgMetadata: unknown,
): boolean {
  return (
    planFromMetadata(userMetadata) === 'paid' ||
    planFromMetadata(orgMetadata) === 'paid'
  );
}

/** Request body for the owner-only plan toggle (`POST /api/clerk/plan`). */
// A discriminated union, not one object: `organizationId` is only meaningful
// for `scope: 'org'` (the dev panel names the org because nothing in the app
// ever sets a Clerk active-org session). `strict()` makes a user-scoped
// payload that names an org an error rather than a silently-ignored field.
export const SetPlanInputSchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('user'), plan: PlanSchema }).strict(),
  z
    .object({
      scope: z.literal('org'),
      plan: PlanSchema,
      organizationId: z.string().min(1).optional(),
    })
    .strict(),
]);
export type SetPlanInput = z.infer<typeof SetPlanInputSchema>;

// ---------------------------------------------------------------------------
// AI tone
//
// A 0–100 dial (0 = reassuring, 100 = curt) steering the voice of AI responses,
// held in Clerk `publicMetadata` — on the user (their own tone) and, independently,
// on the organisation (the workspace default, maintainer-set). Like the plan it is
// only writable from the backend (`POST /api/clerk/tone`).
// ---------------------------------------------------------------------------

/** The AI tone dial: an integer 0–100. */
export const AiToneSchema = z.number().int().min(0).max(100);

/** Read an AI tone from Clerk `publicMetadata`; `null` when absent or malformed. */
export function aiToneFromMetadata(metadata: unknown): number | null {
  const tone = (metadata as { readonly aiTone?: unknown } | null | undefined)
    ?.aiTone;
  const parsed = AiToneSchema.safeParse(tone);
  return parsed.success ? parsed.data : null;
}

/**
 * The tone that applies to a person: their own tone wins, else their active org's,
 * else none. Pure, so it runs on the server and the client. Pass the user's
 * `publicMetadata` and the active org's `publicMetadata` (or `undefined`).
 */
export function effectiveAiTone(
  userMetadata: unknown,
  orgMetadata: unknown,
): number | null {
  return aiToneFromMetadata(userMetadata) ?? aiToneFromMetadata(orgMetadata);
}

/** Request body for the AI-tone toggle (`POST /api/clerk/tone`). */
export const SetAiToneInputSchema = z.object({
  scope: z.enum(['user', 'org']),
  aiTone: AiToneSchema.nullable(),
});
export type SetAiToneInput = z.infer<typeof SetAiToneInputSchema>;
